// =============================================================================
// CONSTANTS - Geothermal Borefield Management Application
// =============================================================================

/**
 * Compute server base URL.
 * In development, Vite proxies /api to localhost:5001 (see vite.config.ts).
 * In production, this should be set via VITE_COMPUTE_URL environment variable.
 */
export const COMPUTE_BASE_URL =
  (import.meta.env.VITE_COMPUTE_URL as string | undefined) ??
  "http://localhost:5001";

/** State plane code entry */
export interface StatePlaneCode {
  readonly code: string;
  readonly name: string;
  readonly fips: string;
}

// State plane codes using FIPS codes to match Python API
export const STATE_PLANE_CODES: readonly StatePlaneCode[] = [
  { code: "2111", name: "Michigan North", fips: "2111" },
  { code: "2112", name: "Michigan Central", fips: "2112" },
  { code: "2113", name: "Michigan South", fips: "2113" },
  { code: "3401", name: "Ohio North", fips: "3401" },
  { code: "3402", name: "Ohio South", fips: "3402" },
  { code: "1201", name: "Illinois East", fips: "1201" },
  { code: "1202", name: "Illinois West", fips: "1202" },
  { code: "1301", name: "Indiana East", fips: "1301" },
  { code: "1302", name: "Indiana West", fips: "1302" },
  { code: "4801", name: "Wisconsin North", fips: "4801" },
  { code: "4802", name: "Wisconsin Central", fips: "4802" },
  { code: "4803", name: "Wisconsin South", fips: "4803" },
] as const;

/** Generic select option — used by picklists across the app */
export interface BoreTypeOption {
  readonly value: string;
  readonly label: string;
}

import type { WellTypeCategory } from "../features/borefields/types";

/** Bore type select option with well-type category */
export interface BoreTypeOptionWithCategory extends BoreTypeOption {
  readonly category: WellTypeCategory;
}

/** Geothermal bore types */
export const GEOTHERMAL_BORE_TYPES: readonly BoreTypeOptionWithCategory[] = [
  { value: "Single U-Tube", label: "Single U-Tube", category: "geothermal" },
  { value: "Double U-Tube", label: "Double U-Tube", category: "geothermal" },
  { value: "Triple U-Tube", label: "Triple U-Tube", category: "geothermal" },
  { value: "Coaxial", label: "Coaxial", category: "geothermal" },
] as const;

/** Oil & gas bore types */
export const OG_BORE_TYPES: readonly BoreTypeOptionWithCategory[] = [
  { value: "Oil Producer", label: "Oil Producer", category: "oil_gas" },
  { value: "Gas Producer", label: "Gas Producer", category: "oil_gas" },
  { value: "Water Injector", label: "Water Injector", category: "oil_gas" },
  { value: "Gas Injector", label: "Gas Injector", category: "oil_gas" },
  {
    value: "SWD (Saltwater Disposal)",
    label: "SWD (Saltwater Disposal)",
    category: "oil_gas",
  },
  { value: "Observation", label: "Observation", category: "oil_gas" },
  {
    value: "Stratigraphic Test",
    label: "Stratigraphic Test",
    category: "oil_gas",
  },
] as const;

/** All bore types — geothermal + O&G */
export const ALL_BORE_TYPES: readonly BoreTypeOptionWithCategory[] = [
  ...GEOTHERMAL_BORE_TYPES,
  ...OG_BORE_TYPES,
] as const;

/** Legacy alias — geothermal bore types only */
export const BORE_TYPES: readonly BoreTypeOption[] = GEOTHERMAL_BORE_TYPES;

/** Well type category labels for UI */
export const WELL_TYPE_CATEGORIES: readonly BoreTypeOption[] = [
  { value: "geothermal", label: "Geothermal" },
  { value: "oil_gas", label: "Oil & Gas" },
] as const;

export const BORE_STATUSES = [
  "Planned",
  "Permitted",
  "Drilling",
  "Completed",
  "Grouted",
  "LoopInstalled",
  "Operational",
  "Suspended",
  "TemporarilyAbandoned",
  "Abandoned",
] as const;

export type BoreStatus = (typeof BORE_STATUSES)[number];

// REMOVED (EPIC_BORE_TO_WELL_CUTOVER.md Phase 6): `DashboardStatus`, `BORE_DASHBOARD_STATUSES`,
// `BOREFIELD_STATUSES` and a SECOND `BorefieldStatus` type. All four had zero importers.
//
// `BORE_DASHBOARD_STATUSES` was one of THREE constants sharing that name with three different
// value sets, and this copy offered `"Construction"` — not a member of `BoreStatus`. It was typed
// `DashboardStatus` (`value: string`), so the compiler could not catch the mismatch. The
// BorefieldDetail copy died with those screens; `features/trajectory/constants.ts` is now the only
// one left, and every value in it IS a legal `BoreStatus`.
//
// `BorefieldStatus` remains declared in `features/borefields/types.ts` — this was a duplicate of
// the name, not a second concept. Canonical well status is `WELL_STATUS_V2`
// (features/wells/components/WellCreationWizard/constants.ts).

export const GEOID_DATUMS: readonly BoreTypeOption[] = [
  { value: "NAD27", label: "NAD27" },
  { value: "NAD83", label: "NAD83" },
  { value: "GEOID18", label: "GEOID18" },
  { value: "GEOID12B", label: "GEOID12B" },
  { value: "GEOID12A", label: "GEOID12A" },
  { value: "EGM2008", label: "EGM2008" },
] as const;

export const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
] as const;

export type USState = (typeof US_STATES)[number];

/** Job type option with icon and description */
export interface JobTypeOption {
  readonly value: string;
  readonly label: string;
  readonly icon: string;
  readonly description: string;
}

// The five well-lifecycle job types (PO 2026-06-28). Each maps to one of the
// three timeline paradigms via JOB_TYPE_TIMELINE_PATTERN below. (`maintenance`
// was renamed/reframed to `treatment` = Bore Treatment; `stimulation` is new.)
export const JOB_TYPES: readonly JobTypeOption[] = [
  {
    value: "construction",
    label: "Drilling (Construction)",
    icon: "Drill",
    description: "Initial drilling and construction of the bore",
  },
  {
    value: "stimulation",
    label: "Hydraulic Stimulation",
    icon: "Zap",
    description:
      "Hydraulic fracturing / stimulation — often SIMOPS across the pad's bores",
  },
  {
    value: "treatment",
    label: "Bore Treatment",
    icon: "Beaker",
    description: "Chemical treatment & flow assurance",
  },
  {
    value: "workover",
    label: "Workover / Intervention",
    icon: "Wrench",
    description: "Intervention, repair, or recompletion of an existing bore",
  },
  {
    value: "abandon",
    label: "Abandonment",
    icon: "XCircle",
    description: "Permanent plugging & abandonment of the bore",
  },
] as const;

/** The three well-lifecycle timeline paradigms a job's pattern maps to:
 *  Drilling, Stimulation (fracking), and everything else. */
export type TimelinePattern = "drilling" | "fracking" | "other";

/** Default timeline pattern per job type (5 job types → 3 paradigms). A job
 *  carries its own `timelinePattern`, defaulted here at creation. */
export const JOB_TYPE_TIMELINE_PATTERN: Readonly<
  Record<string, TimelinePattern>
> = {
  construction: "drilling",
  stimulation: "fracking",
  treatment: "other",
  workover: "other",
  abandon: "other",
};

export function timelinePatternForJobType(jobType: string): TimelinePattern {
  return JOB_TYPE_TIMELINE_PATTERN[jobType] ?? "other";
}

export const JOB_REPORT_TYPES: readonly JobTypeOption[] = [
  {
    value: "drilling-tools",
    label: "Drilling Tools",
    icon: "Drill",
    description: "Document drillstring configuration and BHA",
  },
  {
    value: "casing",
    label: "Casing",
    icon: "Pipe",
    description: "Casing strings, tally, jewelry, and running operations",
  },
  {
    value: "drilling-params",
    label: "Drilling Parameters",
    icon: "Activity",
    description: "Record drilling parameters (RPM, torque, WOB, pump rate)",
  },
  {
    value: "edr-analytics",
    label: "RigSigma",
    icon: "Gauge",
    description: "Analyze an EDR export — rig states, time breakdown, ROP, MSE",
  },
  {
    value: "time-log",
    label: "Time Log",
    icon: "Clock",
    description: "Record activity time and drilling progress",
  },
  {
    value: "cost-log",
    label: "Cost Log",
    icon: "Dollar",
    description: "Track costs, materials, and expenses",
  },
  {
    value: "personnel-log",
    label: "Personnel Log",
    icon: "Users",
    description: "Track personnel time and attendance",
  },
  {
    value: "mud-properties",
    label: "Mud Properties",
    icon: "Droplet",
    description: "Record drilling-fluid properties and measurements",
  },
  {
    value: "mud-additives",
    label: "Mud Additives",
    icon: "Plus",
    description: "Track daily fluid additives, inventory, and costs",
  },
  {
    value: "solids-disposal",
    label: "Slurry Disposal",
    icon: "Truck",
    description: "Document slurry removal and disposal",
  },
  {
    value: "safety",
    label: "Safety",
    icon: "Shield",
    description: "Document safety observations, near misses, and incidents",
  },
  {
    value: "problem",
    label: "NPT/Lessons",
    icon: "AlertCircle",
    description: "Document issues and remedial actions",
  },
  {
    value: "well-control",
    label: "Well Control",
    icon: "Flow",
    description: "Document well control events and response",
  },
  {
    value: "drilling-hazards",
    label: "Drilling Hazards",
    icon: "AlertTriangle",
    description: "Document hazards by depth interval with risk assessment",
  },
  {
    value: "mud-log",
    label: "Formations",
    icon: "Layers",
    description: "Record mud logging data and observations",
  },
  {
    value: "thermal-gradient",
    label: "Thermal Gradient",
    icon: "TrendingDown",
    description: "Record thermal gradient measurements and sensor data",
  },
  {
    value: "loop-pressure-test",
    label: "Loop Pressure Test",
    icon: "Gauge",
    description: "Loop pressure test results + gauge calibration",
  },
  {
    value: "pressure-test",
    label: "Pressure Test",
    icon: "Gauge",
    description: "BOP / FIT / LOT / negative-pressure tests (coming soon)",
  },
  {
    value: "zonal-isolation",
    label: "Zonal Isolation",
    icon: "Pipe",
    description: "Document cementing and zonal isolation operations",
  },
  {
    // value stays "working-fluid" (route + report-type key) until the page is rebuilt
    // as the bore-history Fluid Sample report (see docs/REQ_fluid_sample_report.md).
    value: "working-fluid",
    label: "Fluid Sample",
    icon: "Thermometer",
    description: "Fluid sample results across the bore's history",
  },
  {
    value: "loop-running",
    label: "Loop Running",
    icon: "RefreshCw",
    description: "Document loop running operations",
  },
] as const;

/** TRT step entry */
export interface TrtStep {
  readonly id: string;
  readonly label: string;
}

export const TRT_STEPS: readonly TrtStep[] = [
  { id: "bore-params", label: "Bore Parameters" },
  { id: "formations", label: "Formation Data" },
  { id: "gradient", label: "Thermal Gradient" },
  { id: "test-data", label: "TRT Data" },
  { id: "results", label: "Analysis Results" },
  { id: "save", label: "Save & Export" },
] as const;

export const TRT_CHART_COLORS = {
  primary: "#7c3aed",
  secondary: "#3b82f6",
  accent: "#ef4444",
  gray: "#6b7280",
  success: "#10b981",
  warning: "#f59e0b",
  purge: "rgba(239, 68, 68, 0.1)",
  transient: "rgba(245, 158, 11, 0.1)",
  test: "rgba(16, 185, 129, 0.1)",
} as const;

export const TRT_CHART_HEIGHT = 320 as const;

// =============================================================================
// CIRCUIT MAP CONSTANTS
// =============================================================================

export const CIRCUIT_COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#f97316",
  "#14b8a6",
  "#84cc16",
  "#f43f5e",
  "#06b6d4",
] as const;

export const NODE_TYPES: readonly BoreTypeOption[] = [
  { value: "manifold", label: "Manifold" },
  { value: "header", label: "Header" },
  { value: "junction", label: "Junction" },
] as const;

// =============================================================================
// SLURRY / GROUT TYPE OPTIONS (shared between Common Properties & Zonal Isolation)
// =============================================================================

export const SLURRY_TYPE_OPTIONS: readonly BoreTypeOption[] = [
  { value: "Grout - Neat", label: "Grout - Neat" },
  { value: "Grout - Thermally Enhanced", label: "Grout - Thermally Enhanced" },
  { value: "Cement - Neat", label: "Cement - Neat" },
  {
    value: "Cement - Thermally Enhanced",
    label: "Cement - Thermally Enhanced",
  },
  { value: "Gravel Pack", label: "Gravel Pack" },
  { value: "Sand Pack", label: "Sand Pack" },
  { value: "Clay", label: "Clay" },
] as const;

// =============================================================================
// DRILLING RIG CONSTANTS
// =============================================================================

/** Rig option with optional description */
export interface RigOption {
  readonly value: string;
  readonly label: string;
  readonly description?: string;
}

export const RIG_CLASSES: readonly RigOption[] = [
  {
    value: "light",
    label: "Light Duty",
    description: "Small rigs for shallow bores (<500 ft)",
  },
  {
    value: "medium",
    label: "Medium Duty",
    description: "Standard geothermal drilling rigs (500-1500 ft)",
  },
  {
    value: "heavy",
    label: "Heavy Duty",
    description: "Large rigs for deep bores (>1500 ft)",
  },
  {
    value: "trailer",
    label: "Trailer Mounted",
    description: "Portable trailer-mounted drilling unit",
  },
  {
    value: "truck",
    label: "Truck Mounted",
    description: "Drilling unit mounted on truck chassis",
  },
  {
    value: "track",
    label: "Track Mounted",
    description: "Crawler-mounted for difficult terrain",
  },
] as const;

export const RIG_SUBSTRUCTURE_TYPES: readonly BoreTypeOption[] = [
  { value: "skid", label: "Skid Mounted" },
  { value: "trailer", label: "Trailer Mounted" },
  { value: "truck", label: "Truck Mounted" },
  { value: "self-propelled", label: "Self-Propelled" },
  { value: "crawler", label: "Crawler/Track" },
] as const;

export const RIG_MAST_TYPES: readonly BoreTypeOption[] = [
  { value: "telescopic", label: "Telescopic" },
  { value: "folding", label: "Folding" },
  { value: "fixed", label: "Fixed" },
  { value: "hydraulic", label: "Hydraulic Raise" },
] as const;

export const RIG_FLUID_TYPES: readonly BoreTypeOption[] = [
  { value: "air", label: "Air" },
  { value: "mud", label: "Mud/Water" },
  { value: "foam", label: "Foam" },
  { value: "dual", label: "Air/Mud Capable" },
] as const;

export const RIG_POWER_TYPES: readonly BoreTypeOption[] = [
  { value: "diesel", label: "Diesel" },
  { value: "electric", label: "Electric" },
  { value: "hybrid", label: "Diesel-Electric Hybrid" },
  { value: "hydraulic", label: "Hydraulic" },
] as const;

/** Equipment role with icon */
export interface EquipmentRoleOption {
  readonly value: string;
  readonly label: string;
  readonly icon: string;
}

export const RIG_EQUIPMENT_ROLES: readonly EquipmentRoleOption[] = [
  { value: "mud-pump", label: "Mud Pump", icon: "Pump" },
  { value: "shaker", label: "Shale Shaker", icon: "Filter" },
  { value: "degasser", label: "Degasser", icon: "Wind" },
  { value: "desander", label: "Desander", icon: "Filter" },
  { value: "desilter", label: "Desilter", icon: "Filter" },
  { value: "centrifuge", label: "Centrifuge", icon: "RefreshCw" },
  { value: "mud-cleaner", label: "Mud Cleaner", icon: "Filter" },
  { value: "top-drive", label: "Top Drive", icon: "RotateCw" },
  { value: "rotary-table", label: "Rotary Table", icon: "Circle" },
  { value: "drawworks", label: "Drawworks", icon: "Package" },
  { value: "bop", label: "BOP Stack", icon: "Shield" },
  { value: "accumulator", label: "Accumulator", icon: "Battery" },
  { value: "power-tongs", label: "Power Tongs", icon: "Tool" },
  { value: "iron-roughneck", label: "Iron Roughneck", icon: "Tool" },
  { value: "pipe-handler", label: "Pipe Handler", icon: "Move" },
  { value: "generator", label: "Generator", icon: "Zap" },
  { value: "compressor", label: "Air Compressor", icon: "Wind" },
  { value: "mud-tank", label: "Mud Tank", icon: "Database" },
  { value: "water-tank", label: "Water Tank", icon: "Droplet" },
] as const;
